import React, { useState } from 'react';
import { Typography, Button } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { Spinner } from '../../components/Spinner';
import { useExplainPrediction } from '../../core/modules/book/hooks/useExplainPrediction';
import AiSummaryBar from './AiSummaryBar';
import { renderExplanation } from './renderAiMarkup';

const { Text } = Typography;

const ACCENT = '#3b2984';

interface IPredictionExplanationProps {
  book: object;
}

const toNumber = (value: unknown): number | null => {
  const num = Number(value);

  return Number.isFinite(num) ? num : null;
};

// "Summarize title details": explains the title's prediction from its real model signals.
const PredictionExplanation: React.FC<IPredictionExplanationProps> = ({ book }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { mutate, data, isLoading, isError } = useExplainPrediction();

  const predictedUnits = toNumber(book['Projected Sales']);
  const successProbability = toNumber(book['Confidence']);

  // No prediction to explain (e.g. Royal Road rows without a model score).
  if (predictedUnits === null || successProbability === null) {
    return null;
  }

  const generate = () => {
    mutate({
      amazonasin: book['ASIN'],
      title: book['Title'],
      author: book['Author'],
      genre: book['Genre'] ?? null,
      success_probability: successProbability,
      predicted_units: predictedUnits
    });
  };

  // Generate on the first open only; reopening shows the same summary.
  const handleToggle = () => {
    if (!isOpen && !data && !isLoading) {
      generate();
    }
    setIsOpen((open) => !open);
  };

  return (
    <AiSummaryBar
      className='ai-summary-title-details'
      title='Summarize title details'
      isOpen={isOpen}
      onToggle={handleToggle}
    >
      {isLoading && (
        <div className='ai-summary-loading'>
          <Spinner size='default' spinning indicator={<LoadingOutlined style={{ fontSize: 22 }} />} />
          <Text type='secondary' style={{ fontSize: 12 }}>Analysing the prediction & book metadata…</Text>
        </div>
      )}
      {isError && !isLoading && (
        <div>
          <Text type='danger'>Unable to generate summary right now. Please try again later.</Text>
          <Button type='link' size='small' onClick={generate}>Retry</Button>
        </div>
      )}
      {data && !isLoading && (
        <>
          <div className='ai-summary-text'>{renderExplanation(data.explanation, ACCENT)}</div>
          <Text type='secondary' className='ai-summary-footer'>
            AI-generated from Brenda&apos;s model signals
          </Text>
        </>
      )}
    </AiSummaryBar>
  );
};

export default PredictionExplanation;
